import { useState, useCallback, useEffect } from 'react';
import { Loader2, Eye, CheckCircle2, Grid3x3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { recurringCostMatchingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { RecurringCostMatchChainControl } from './RecurringCostMatchChainControl';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const CELL_STYLES = {
  matched: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  expected_unmatched: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  no_instance: 'bg-slate-50 text-slate-400 dark:bg-slate-900/20 dark:text-slate-600',
};

const CELL_SYMBOL = {
  matched: '✓',
  expected_unmatched: '!',
  no_instance: '·',
};

function CoverageGrid({ grid }) {
  if (!grid) return null;
  const allRowGroups = [
    ...grid.properties.map((p) => ({ label: p.name || p.property_id, rows: p.rows })),
    grid.entity_level.length ? { label: 'Entity-level costs', rows: grid.entity_level } : null,
  ].filter(Boolean);

  return (
    <div className="space-y-4">
      {allRowGroups.length === 0 && grid.not_instance_tracked.length === 0 && (
        <p className="text-sm text-muted-foreground">No recurring costs entered yet.</p>
      )}
      {allRowGroups.map((group) => (
        <div key={group.label} className="overflow-x-auto">
          <div className="text-xs font-medium mb-1">{group.label}</div>
          <table className="w-full text-xs" data-testid="recurring-cost-coverage-grid">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1 pr-2">Cost type</th>
                {grid.months.map((m) => (
                  <th key={m} className="py-1 px-1 text-center font-mono">{m}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {group.rows.map((row) => (
                <tr key={`${row.property_id || row.entity_id}-${row.cost_type}`} className="border-t">
                  <td className="py-1 pr-2">{row.cost_type_label || row.cost_type}</td>
                  {row.cells.map((cell) => (
                    <td key={cell.month} className="py-1 px-1 text-center">
                      <span
                        className={`inline-flex items-center justify-center h-5 w-5 rounded ${CELL_STYLES[cell.status]}`}
                        title={`${row.cost_type_label || row.cost_type} · ${cell.month} · ${cell.status.replace('_', ' ')}`}
                      >
                        {CELL_SYMBOL[cell.status]}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {grid.not_instance_tracked.length > 0 && (
        <div>
          <div className="text-xs font-medium mb-1">Not instance-tracked (per-stay, one-off, or percentage-rate costs)</div>
          <div className="flex flex-wrap gap-1">
            {grid.not_instance_tracked.map((c) => (
              <Badge key={c.recurring_cost_id} variant="outline" className="text-[10px] px-1 py-0">
                {c.cost_type} ({c.reason})
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Consistency Census + Preview/Apply + per-property coverage grid for
// recurring-cost reconciliation (see services/recurring_cost_matcher.py,
// services/recurring_cost_match_chain.py). Census/Preview read the latest
// COMPLETE chain -- a budget-boxed chunked chain over the candidate-
// transaction walk (CLAUDE.md section 13); RecurringCostMatchChainControl
// drives/polls it and this card re-fetches census/preview/grid once it
// completes. The coverage grid itself is unaffected -- it only ever reads
// confirmed matches + instances, both bounded/sparse cohorts.
export function RecurringCostCoverageCard() {
  const [census, setCensus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [grid, setGrid] = useState(null);
  const [loadingCensus, setLoadingCensus] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [applying, setApplying] = useState(false);

  const loadGrid = useCallback(async () => {
    setLoadingGrid(true);
    try {
      const { data } = await recurringCostMatchingAPI.getCoverageGrid(6);
      setGrid(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingGrid(false);
    }
  }, []);

  useEffect(() => { loadGrid(); }, [loadGrid]);

  const loadCensus = useCallback(async () => {
    setLoadingCensus(true);
    try {
      const { data } = await recurringCostMatchingAPI.getCensus();
      setCensus(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingCensus(false);
    }
  }, []);

  useEffect(() => { loadCensus(); }, [loadCensus]);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const { data } = await recurringCostMatchingAPI.getPreview();
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  // Deliberately does not also refresh `preview` here: the preview panel
  // opens only on an explicit "Preview matches" click, so a fresh chain run
  // shouldn't reveal it -- the user re-clicks Preview to see up-to-date
  // proposed matches after a run.
  const onRunCompleted = useCallback(() => {
    loadCensus();
    loadGrid();
  }, [loadCensus, loadGrid]);

  const apply = useCallback(async () => {
    if (!preview?.proposed_matches?.length) return;
    setApplying(true);
    try {
      const { data } = await recurringCostMatchingAPI.apply(preview.proposed_matches);
      const failedCount = data.failed?.length ?? 0;
      const summary = `Applied ${data.applied.length} match(es)${data.skipped.length ? `, skipped ${data.skipped.length}` : ''}`;
      if (failedCount > 0) {
        // A failed pair is a first-class outcome, not swallowed into the
        // applied/skipped counts -- it means that match is now stuck at
        // status="confirmed" with an unstamped transaction (see
        // RecurringCostMatchRepairPanel on the Diagnostics page) and needs
        // the repair sweep, so surface it loudly rather than as a quiet
        // count difference the operator has to notice is missing.
        toast.error(`${summary}, ${failedCount} FAILED -- see Diagnostics > Recurring Cost Match Repair`);
      } else {
        toast.success(summary);
      }
      setPreview(null);
      await Promise.all([loadCensus(), loadGrid()]);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  }, [preview, loadCensus, loadGrid]);

  const counts = census?.bucket_counts || {};

  return (
    <Card data-testid="recurring-cost-coverage-card">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="flex items-center gap-2">
          <Grid3x3 className="h-4 w-4" />
          Recurring Costs
        </CardTitle>
        <RecurringCostMatchChainControl onRunCompleted={onRunCompleted} />
      </CardHeader>
      <CardContent className="space-y-4">
        {loadingGrid && !grid ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <CoverageGrid grid={grid} />
        )}

        {loadingCensus && !census ? null : census && !census.ready ? (
          <p className="text-sm text-muted-foreground pt-2 border-t">
            No completed matcher run yet -- click "Run matcher" above to compute the consistency census.
          </p>
        ) : census && census.ready && (
          <div className="pt-2 border-t">
            <p className="text-sm text-muted-foreground mb-2">
              {fmtInt(census.open_instance_count)} open instance(s), {fmtInt(census.confirmed_instance_count)} already confirmed,
              {' '}{fmtInt(census.candidate_txn_count)} candidate transaction(s)
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className={`rounded-md p-2 ${CELL_STYLES.matched}`}>
                <div className="text-xs font-medium">Matched</div>
                <div className="text-lg font-mono">{fmtInt(counts.matched)}</div>
              </div>
              <div className={`rounded-md p-2 ${CELL_STYLES.expected_unmatched}`}>
                <div className="text-xs font-medium">Expected, unmatched</div>
                <div className="text-lg font-mono">{fmtInt(counts.expected_unmatched)}</div>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={loadPreview} disabled={loadingPreview}>
            {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            Preview matches
          </Button>
          {preview && preview.ready && (
            <Button size="sm" onClick={apply} disabled={applying || !preview.proposed_matches?.length}>
              {applying ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Apply {preview.count || 0} match(es)
            </Button>
          )}
        </div>

        {preview && !preview.ready && (
          <p className="text-sm text-muted-foreground">No completed matcher run yet -- click "Run matcher" above first.</p>
        )}

        {preview && preview.ready && (
          <div className="overflow-x-auto">
            {preview.proposed_matches.length === 0 ? (
              <p className="text-sm text-muted-foreground">No proposed matches -- nothing new to apply.</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1 pr-2">Month</th>
                    <th className="py-1 pr-2 text-right">Amount</th>
                    <th className="py-1 pr-2">Txn date</th>
                    <th className="py-1 pr-2">Reason</th>
                    <th className="py-1 pr-2">Counterparty signal</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.proposed_matches.map((m) => (
                    <tr key={`${m.expected_instance_id}-${m.txn_id}`} className="border-t">
                      <td className="py-1 pr-2 font-mono">{m.month}</td>
                      <td className="py-1 pr-2 text-right font-mono">{m.amount}</td>
                      <td className="py-1 pr-2">{m.txn_date}</td>
                      <td className="py-1 pr-2">
                        <Badge variant="outline" className="text-[10px] px-1 py-0">{m.match_reason}</Badge>
                      </td>
                      <td className="py-1 pr-2 text-muted-foreground">{m.counterparty_signal || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
